import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getApiLanguage } from "src/services/commonLanguageHeader";
import {
  createVehicleMake,
  createVehicleModel,
  deleteVehicleMake,
  deleteVehicleModel,
  getVehicleMakeLookup,
  getVehicleMakesList,
  getVehicleModelsList,
  updateVehicleMake,
  updateVehicleModel,
  type VehicleMakePayload,
  type VehicleModelPayload,
  type VehicleSetupListParams,
} from "src/services/administrator/vehicleSetupService";
import { useGeneralRefetchOverride } from "src/queries/general.queries";
import { QueryNames } from "src/queries/queries.constant";

export const useQueryGetVehicleMakesList = (
  params: VehicleSetupListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.vehicleMakeList, params, language],
      queryFn: () => getVehicleMakesList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false,
  );
};

export const useQueryGetVehicleModelsList = (
  params: VehicleSetupListParams = {},
  options: { enabled?: boolean } & Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useGeneralRefetchOverride(
    useQuery({
      queryKey: [...QueryNames.vehicleModelList, params, language],
      queryFn: () => getVehicleModelsList(params, language),
      refetchOnMount: true,
      retry: false,
      ...options,
    }),
    options.enabled !== false,
  );
};

export const useQueryGetVehicleMakeLookup = (
  enabled = true,
  options: Record<string, unknown> = {},
) => {
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useQuery({
    queryKey: [...QueryNames.vehicleMakeLookup, language],
    queryFn: () => getVehicleMakeLookup(language),
    enabled,
    retry: false,
    ...options,
  });
};

export const useMutationCreateVehicleMake = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: VehicleMakePayload) =>
      createVehicleMake(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleMakeList });
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleMakeLookup });
    },
  });
};

export const useMutationUpdateVehicleMake = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: Partial<VehicleMakePayload>;
    }) => updateVehicleMake(id, payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleMakeList });
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleMakeLookup });
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleModelList });
    },
  });
};

export const useMutationDeleteVehicleMake = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteVehicleMake(id, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleMakeList });
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleMakeLookup });
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleModelList });
    },
  });
};

export const useMutationCreateVehicleModel = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (payload: VehicleModelPayload) =>
      createVehicleModel(payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleModelList });
    },
  });
};

export const useMutationUpdateVehicleModel = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string | number;
      payload: Partial<VehicleModelPayload>;
    }) => updateVehicleModel(id, payload, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleModelList });
    },
  });
};

export const useMutationDeleteVehicleModel = () => {
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const language = getApiLanguage(i18n.language);

  return useMutation({
    mutationFn: (id: string | number) => deleteVehicleModel(id, language),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QueryNames.vehicleModelList });
    },
  });
};
